import { I18n } from 'react-redux-i18n-lite';
import _ from 'lodash';
import * as FileSystemService from '../../../services/file/fileSystemService';
import * as messageAlert from '../../messageAlertAction';
import { requireFromElectron } from '../../../services/crossPlatform/crossPlatformService';
import { getLogger } from '../../../services/logger';
import { loadSmartWindowAndHideMainWindow, openSmartWindow, openWebViewWindow } from '../../../electron/windowManager';
import { BOOK_LAUNCH_MODES, WEB_TYPES } from '../../../const';
import {
  createUnitContext,
  getContentBookByBookIdAndClassId,
  isStoreContentBookDownloaded,
  isStoreContentBookSaving,
  isStoreContentBookSynchronizing
} from '../../../utils/contentUtils';
import { getCdnIndexPath, getUnitCmi5FilePath, getUnitScaFilePath } from '../../../utils/contentPathUtils';
import { Cmi5 } from '../../../services/cmi5/cmi5';
import * as contentPathUtils from '../../../utils/contentPathUtils';
import { getRefreshTokenFromStorage, getTokenFromStorage } from '../../../shared';

const electron = requireFromElectron('electron');

const debug = getLogger('actions:cross-platform:book:bookActions.electron');

export let openResource = openBookConfig => {
  return async (dispatch, getState) => {
    const unitContext = getUnitContext(openBookConfig);
    const cmi5Lesson = await getCmi5Lesson(unitContext, openBookConfig.lesson.activityId);
    const book = openBookConfig.book;
    const contentBooks = getState().content.contentBooks;
    const downloading = isDownloading(contentBooks, book);
    if (downloading) {
      dispatch(messageAlert.warning(`${I18n.t('common.bookDownloading')}`));
      return;
    } else if (!cmi5Lesson) {
      dispatch(messageAlert.warning(`${I18n.t('common.bookNotAvailable')}`));
      return;
    }

    const launchMode = cmi5Lesson.mode;
    if (launchMode === BOOK_LAUNCH_MODES.SMART) {
      dispatch(openSmartResource(openBookConfig));
    } else if (launchMode === BOOK_LAUNCH_MODES.NONE) {
      dispatch(openRegularResource(openBookConfig, cmi5Lesson.launcher));
    } else {
      throw new Error('Unsupported launch mode');
    }
  };
};

export let openAsset = (lesson, unitContext) => {
  return async dispatch => {
    const { activityId } = lesson;
    const lessonLauncherPath = await getCmi5LauncherLesson(unitContext, activityId);
    dispatch(openLocalAsset(lessonLauncherPath));
  };
};

export let deleteBookPostAction = (classId = '', bookId = -1, bookActivityId = '', userId) => {
  return async (dispatch, getState) => {
    if (bookActivityId) {
      const singleUnitModeUnitId = getState().router.location.query.unitId;
      FileSystemService.removeDownloadingContent(userId, classId, bookId, bookActivityId, singleUnitModeUnitId);
      // return dispatch(contentActions.synchronizeContentDownload(userId));
    }
  };
};

function getBookUrl(bookIndexPath, unitScaPath) {
  return `file://${bookIndexPath}?smartBookUrl=file:///${unitScaPath}`;
}

function getUnitContext(openBookConfig) {
  const userId = openBookConfig.account.user._id;
  const classId = openBookConfig.classroom.id;
  const bookId = openBookConfig.book.id;
  const bookActivityId = openBookConfig.book.activityId;
  const unitActivityId = openBookConfig.unit.activityId;
  const unitContext = createUnitContext(userId, classId, bookId, bookActivityId, unitActivityId);
  return unitContext;
}

async function getCmi5Json(unitContext) {
  const cmi5File = getUnitCmi5FilePath(unitContext);
  return await FileSystemService.readJsonFile(cmi5File);
}

async function getCmi5Lesson(unitContext, activityId) {
  const cmi5Json = await getCmi5Json(unitContext);
  if (cmi5Json) {
    const cmi5Lesson = new Cmi5().getCmi5Lesson(cmi5Json, activityId);
    return cmi5Lesson;
  }
}

async function getCmi5LauncherLesson(unitContext, activityId) {
  const cmi5Json = await getCmi5Json(unitContext);
  if (cmi5Json) {
    const lessonLauncherPath = new Cmi5().getCmi5LauncherLesson(cmi5Json, activityId);
    return lessonLauncherPath;
  }
}

function openAssetInElectronWindow(resourceFilePath) {
  const { shell } = electron;
  shell.openItem(resourceFilePath);
}

function openAssetHtmlInElectronWindow(activityId, resourceFilePath) {
  const userDataPath = contentPathUtils.getClassroomAppPath();
  resourceFilePath = resourceFilePath.replace(userDataPath, '');
  const url = `http://localhost:9990${resourceFilePath}`;

  openWebViewWindow(activityId, url);
}

async function assetFileExist(resourceFilePath) {
  return FileSystemService.existsFile(resourceFilePath);
}

function isBookDownloaded(contentBooks, book, classId) {
  const storeContentBook = getContentBookByBookIdAndClassId(contentBooks, book.id, classId);
  const isStoreBookDownloaded = isStoreContentBookDownloaded(book, contentBooks);
  return isStoreBookDownloaded && !storeContentBook.isSynchronizing;
}

function isDownloading(contentBooks, book) {
  return isStoreContentBookSaving(book, contentBooks) || isStoreContentBookSynchronizing(book, contentBooks);
}

function openLocalAsset(book, filePath) {
  return async (dispatch, getState) => {
    const contentBooks = getState().content.contentBooks;
    const downloading = isDownloading(contentBooks, book);
    if (downloading) {
      dispatch(messageAlert.warning(`${I18n.t('common.bookDownloading')}`));
    } else if (await assetFileExist(filePath)) {
      try {
        const extension = _.last(filePath.split('.'));
        const format = [WEB_TYPES.HTML.extension, WEB_TYPES.HTM.extension];
        if (_.includes(format, extension)) {
          openAssetHtmlInElectronWindow(book.activityId, filePath);
        } else {
          openAssetInElectronWindow(filePath);
        }
      } catch (err) {
        debug.error('Error opening resource %s', filePath, err);
      }
    } else {
      dispatch(messageAlert.warning(`${I18n.t('common.bookNotAvailable')}`));
    }
  };
}

function openRegularResource(openBookConfig, launcherUrl) {
  return async (dispatch, getState) => {
    const classId = openBookConfig.classroom.id;
    const book = openBookConfig.book;
    const contentBooks = getState().content.contentBooks;
    const bookDownloaded = isBookDownloaded(contentBooks, book, classId);
    const downloading = isDownloading(contentBooks, book);
    if (bookDownloaded) {
      dispatch(openLocalAsset(book, launcherUrl));
    } else if (downloading) {
      dispatch(messageAlert.warning(`${I18n.t('common.bookDownloading')}`));
    } else {
      dispatch(messageAlert.warning(`${I18n.t('common.bookNotAvailable')}`));
    }
  };
}

function openSmartResource(openBookConfig) {
  return async (dispatch, getState) => {
    const classId = openBookConfig.classroom.id;
    const book = openBookConfig.book;
    const user = openBookConfig.account.user;
    const lesson = openBookConfig.lesson;
    const contentBooks = getState().content.contentBooks;
    const bookDownloaded = isBookDownloaded(contentBooks, book, classId);
    const downloading = isDownloading(contentBooks, book);
    const unitContext = getUnitContext(openBookConfig);
    const bookCdnIndexPath = getCdnIndexPath(unitContext);
    const unitScaPath = getUnitScaFilePath(unitContext);
    user.authToken = getTokenFromStorage();
    user.refreshToken = getRefreshTokenFromStorage();
    if (bookDownloaded) {
      try {
        await FileSystemService.updateScaWithLaunchParameters(
          unitScaPath,
          lesson,
          user,
          openBookConfig.questionActivityId || null
        );
        /* await FileSystemService.updateScaWithLaunchParameters(unitScaPath, lesson, user);*/

        const url = getBookUrl(bookCdnIndexPath, unitScaPath);

        if (openBookConfig.ownWindow) {
          loadSmartWindowAndHideMainWindow(book.activityId, url);
          return;
        }

        openSmartWindow(openBookConfig.questionActivityId || book.activityId, url);
      } catch (err) {
        debug.error('Error opening book: %s', err.stack);
      }
    } else if (downloading) {
      dispatch(messageAlert.warning(`${I18n.t('common.bookDownloading')}`));
    } else {
      dispatch(messageAlert.warning(`${I18n.t('common.bookNotAvailable')}`));
    }
  };
}
